/**
 * Payer Docs Center Page
 *
 * Landing: card-based overview of all payers with search.
 * Drilldown: tabbed intelligence briefing for a single payer.
 */

import React, { useState } from 'react';
import { Box, Tab, Tabs, Typography, useTheme } from '@mui/material';
import { usePayerContracts } from './hooks/usePayerContracts';
import { PayerChipBar } from './components/PayerChipBar';
import { PayerOverviewGrid } from './components/PayerOverviewGrid';
import { BriefingTab } from './components/BriefingTab';
import { RatesTab } from './components/RatesTab';
import { RulesTab } from './components/RulesTab';
import { ReferenceTab } from './components/ReferenceTab';

export default function PayerDocsCenterPage() {
  const theme = useTheme();
  const { payers, selectedPayer, selectedPayerId, setSelectedPayerId, isLoading } = usePayerContracts();
  const [activeTab, setActiveTab] = useState(0);

  // Empty state
  if (!isLoading && payers.length === 0) {
    return (
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
          Payer Documentation Center
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Contract intelligence for your practice
        </Typography>
        <Box
          sx={{
            maxWidth: 520,
            mx: 'auto',
            p: 5,
            textAlign: 'center',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
            Get started with your payer contracts
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
            Send us your payer contracts, fee schedules, and provider manuals and
            we'll extract everything into a structured, searchable format for your practice.
          </Typography>
          <Typography
            component="a"
            href="mailto:contact@jotpsych.com?subject=Payer%20Contract%20Documents"
            variant="body1"
            sx={{
              color: 'primary.main',
              fontWeight: 600,
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            contact@jotpsych.com
          </Typography>
        </Box>
      </Box>
    );
  }

  // ─── Overview Mode (no payer selected) ───────────────────────────────────

  if (!selectedPayer) {
    return (
      <Box>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.25 }}>
            Payer Documentation Center
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {payers.length} contract{payers.length !== 1 ? 's' : ''} on file
          </Typography>
        </Box>

        {/* Search */}
        <PayerChipBar
          payers={payers}
          selectedPayerId={null}
          onSelectPayer={(id) => {
            if (id) {
              setSelectedPayerId(id);
              setActiveTab(0);
            }
          }}
        />

        {/* Payer Cards */}
        <PayerOverviewGrid
          payers={payers}
          onSelectPayer={(id) => {
            setSelectedPayerId(id);
            setActiveTab(0);
          }}
        />

        {/* Rate comparison shortcut */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1.5, mb: 1.5, display: 'block' }}>
            Rate Comparison
          </Typography>
          <RatesTab payers={payers} selectedPayer={null} />
        </Box>
      </Box>
    );
  }

  // ─── Payer Detail Mode ───────────────────────────────────────────────────

  return (
    <Box>
      {/* Back nav + payer title */}
      <PayerChipBar
        payers={payers}
        selectedPayerId={selectedPayerId}
        onSelectPayer={(id) => {
          setSelectedPayerId(id);
          setActiveTab(0);
        }}
      />

      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.25 }}>
          {selectedPayer.payer.name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {selectedPayer.payer.legal_entity} &middot; {selectedPayer.payer.payer_type.replace(/_/g, ' ')} &middot; {selectedPayer.payer.state}
        </Typography>
      </Box>

      {/* Tab Navigation */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        textColor="primary"
        indicatorColor="primary"
        sx={{
          mb: 3,
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.9rem',
            minHeight: 40,
            px: 2,
          },
        }}
      >
        <Tab label="Briefing" />
        <Tab label="Rates" />
        <Tab label="Rules" />
        <Tab label="Reference" />
      </Tabs>

      {/* Tab Content */}
      <Box>
        {activeTab === 0 && <BriefingTab payer={selectedPayer} />}
        {activeTab === 1 && <RatesTab payers={payers} selectedPayer={selectedPayer} />}
        {activeTab === 2 && <RulesTab payer={selectedPayer} />}
        {activeTab === 3 && <ReferenceTab payer={selectedPayer} />}
      </Box>
    </Box>
  );
}
